import { useChatStore } from '../store/chat.store';
import { apiFetch } from './api';
import { streamEventSchema } from '@netsuite-ai/schemas';
import type { MessageBlock } from '@netsuite-ai/types';
import { useUiStore } from '../store/ui.store';

/**
 * The turn currently streaming. A sent message and a block resolve can never
 * overlap — the composer is gated while either runs — so one slot is enough
 * for the stop button to reach whichever is live.
 */
let activeTurn: AbortController | null = null;

export function beginTurn(): AbortController {
  activeTurn?.abort(); // defensive — never strand a previous controller
  activeTurn = new AbortController();
  return activeTurn;
}

export function endTurn(controller: AbortController) {
  // Identity check: a slow-finishing turn must not clear a newer one's slot.
  if (activeTurn === controller) activeTurn = null;
}

/** Stops whatever is streaming — a sent message or a resumed gate. */
export function abortActiveTurn() {
  activeTurn?.abort();
}

/**
 * Reads an SSE body and dispatches events into the chat store. Shared by
 * sendMessage / retryTurn / resolveBlock. Uses getState() (stable actions) so
 * it isn't a hook and doesn't subscribe anything to the store.
 */
export async function consumeStream(
  body: ReadableStream<Uint8Array>,
  turnIdRef: { current: string },
  conversationId: string,
) {
  const s = () => useChatStore.getState();
  // true only while the user is still looking at the conversation we're streaming into
  const isActive = () => s().activeConversationId === conversationId;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? ''; // keep the trailing partial line

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;

      let raw: unknown;
      try {
        raw = JSON.parse(line.slice(6));
      } catch {
        continue;
      }

      // title_update is outside the StreamEvent union.
      if (
        raw !== null &&
        typeof raw === 'object' &&
        (raw as Record<string, unknown>).type === 'title_update'
      ) {
        const r = raw as { conversationId: string; title: string };
        s().updateConversationTitle(r.conversationId, r.title);
        continue;
      }

      const parsed = streamEventSchema.safeParse(raw);
      if (!parsed.success) continue;
      const event = parsed.data;

      // block_status is independent of the active turn — dispatch by blockId.
      if (event.type === 'block_status') {
        s().applyBlockStatus(event.blockId, event.status, event.result);
        continue;
      }

      // Everything below writes into the *active* message list, so it must only
      // run while this stream's conversation is the active one. We still keep
      // draining the reader so the backend request completes & persists.
      if (!isActive()) continue;

      if (event.type === 'turn_start') {
        turnIdRef.current = event.turnId;
        s().startStreamingMessage(turnIdRef.current);
      }
      const tid = turnIdRef.current;
      if (!tid) continue;

      if (event.type === 'text_delta') s().appendTextDelta(tid, event.delta);
      else if (event.type === 'block_start')
        s().startBlock(tid, event.blockId, {
          type: event.blockType,
          id: event.blockId,
        } as unknown as MessageBlock);
      else if (event.type === 'block_delta')
        s().applyBlockDelta(
          tid,
          event.blockId,
          event.delta as Partial<MessageBlock>,
        );
      else if (event.type === 'tool_call_start')
        s().startToolStep(tid, event.toolId, event.label);
      else if (event.type === 'tool_call_end') {
        const { type: _t, toolId, blockId: _b, ...outcome } = event;
        s().endToolStep(tid, toolId, outcome);
      }
      else if (event.type === 'done') s().finalizeMessage(tid, 'complete');
      else if (event.type === 'error') s().finalizeMessage(tid, 'error');
    }
  }
}

/**
 * Resolve a gate block (approve/discard) and stream the resumed run back into
 * the same assistant message. requestId is the idempotency key.
 */
export async function resolveBlock(
  blockId: string,
  action: 'approve' | 'discard',
  edits?: Record<string, unknown>,
) {
  const conversationId = useChatStore.getState().activeConversationId ?? '';
  const requestId = crypto.randomUUID();
  const controller = beginTurn();
  const turnIdRef = { current: '' };

  // A resumed run is a turn like any other — flag it so the composer swaps to
  // Stop, the same way sendMessage does.
  if (conversationId) useUiStore.getState().startStreaming(conversationId);

  try {
    const res = await apiFetch(`/api/chat/blocks/${blockId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ action, edits, requestId }),
      signal: controller.signal,
    });
    // Non-SSE responses (409 already-resolved, idempotent JSON replay) have no
    // `data:` lines — consumeStream simply reads nothing and returns.
    if (!res.ok || !res.body) return;
    await consumeStream(res.body, turnIdRef, conversationId);
  } catch (err) {
    // Callers await this with no catch and flip their own busy flag afterwards,
    // so a rethrown abort would strand the button. Swallow stops only.
    if (!controller.signal.aborted) throw err;
    if (turnIdRef.current)
      useChatStore.getState().finalizeMessage(turnIdRef.current, 'cancelled');
  } finally {
    endTurn(controller);
    if (conversationId) useUiStore.getState().stopStreaming(conversationId);
  }
}
